import { useState } from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartChange: (date: string) => void;
  onEndChange: (date: string) => void;
  onFilter: () => void;
  onReset: () => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

export function DateRangePicker({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  onFilter,
  onReset,
  isLoading,
  isFiltered,
}: DateRangePickerProps) {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const formatDisplay = (dateStr: string) => {
    if (!dateStr) return 'Seleccionar';
    return format(new Date(dateStr + 'T00:00:00'), "d MMM, yy", { locale: es });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={startOpen} onOpenChange={setStartOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "h-9 justify-start text-left font-normal text-xs gap-2 min-w-[140px]",
              !startDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {startDate ? formatDisplay(startDate) : 'Fecha inicio'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={startDate ? new Date(startDate + 'T00:00:00') : undefined}
            onSelect={(date) => {
              if (date) {
                onStartChange(format(date, 'yyyy-MM-dd'));
                setStartOpen(false);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <span className="text-xs text-muted-foreground">→</span>

      <Popover open={endOpen} onOpenChange={setEndOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "h-9 justify-start text-left font-normal text-xs gap-2 min-w-[140px]",
              !endDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {endDate ? formatDisplay(endDate) : 'Fecha fin'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={endDate ? new Date(endDate + 'T00:00:00') : undefined}
            onSelect={(date) => {
              if (date) {
                onEndChange(format(date, 'yyyy-MM-dd'));
                setEndOpen(false);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <Button
        size="sm"
        variant="gradient"
        className="h-9 text-xs font-bold gap-1.5"
        onClick={onFilter}
        disabled={!startDate || !endDate || isLoading}
      >
        {isLoading ? 'Cargando...' : 'Filtrar'}
      </Button>

      {isFiltered && (
        <Button
          size="sm"
          variant="ghost"
          className="h-9 text-xs gap-1.5 text-muted-foreground"
          onClick={onReset}
        >
          <X className="h-3 w-3" />
          Reset
        </Button>
      )}
    </div>
  );
}
